import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as CognitiveIcon,
  Dashboard as UXIcon,
  Code as PromptDNAIcon,
  Person as IdentityIcon,
  AutoAwesome as SemanticMapIcon,
} from '@mui/icons-material';
import type { Persona } from '@personawiz/shared';

interface PersonaCardProps {
  persona: Persona;
  expanded?: boolean;
}

export default function PersonaCard({ persona, expanded = false }: PersonaCardProps) {
  const { coreIdentity, cognitiveProfile, uxBlueprint, promptDNA, semanticMap } = persona;

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Chip label={coreIdentity.userRole} size="small" color="primary" variant="outlined" />
            <Chip label={coreIdentity.useCase} size="small" variant="outlined" sx={{ maxWidth: 200 }} />
          </Stack>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {coreIdentity.personaName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {coreIdentity.description}
          </Typography>
        </Box>

        {/* Core Identity */}
        <Accordion defaultExpanded={expanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IdentityIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Core Identity
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="primary.main" fontWeight={600}>
                    North Star Metric
                  </Typography>
                  <Typography variant="body1">{coreIdentity.northStarMetric}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Primary Goals
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                    {coreIdentity.primaryGoals.map((goal, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                        {goal}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Pain Points
                  </Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2 }}>
                    {coreIdentity.painPoints.map((point, i) => (
                      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                        {point}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Cognitive Profile */}
        <Accordion defaultExpanded={expanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CognitiveIcon color="secondary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Cognitive Profile
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Thinking Style
                  </Typography>
                  <Chip label={cognitiveProfile.thinkingStyle} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Processing Mode
                  </Typography>
                  <Chip label={cognitiveProfile.processingMode} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Decision Style
                  </Typography>
                  <Chip label={cognitiveProfile.decisionStyle} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Attention Span
                  </Typography>
                  <Chip label={cognitiveProfile.attentionSpan} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Risk Tolerance
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={cognitiveProfile.riskTolerance}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {cognitiveProfile.riskTolerance}%
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* UX Blueprint */}
        <Accordion defaultExpanded={expanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UXIcon color="info" />
              <Typography variant="subtitle1" fontWeight={600}>
                UX Blueprint
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Navigation Style
                  </Typography>
                  <Chip label={uxBlueprint.navigationStyle} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Visual Complexity
                  </Typography>
                  <Chip label={uxBlueprint.visualComplexity} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Interaction Pace
                  </Typography>
                  <Chip label={uxBlueprint.interactionPace} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Guidance Level
                  </Typography>
                  <Chip label={uxBlueprint.guidanceLevel} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Information Density
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={uxBlueprint.informationDensity * 10}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {uxBlueprint.informationDensity}/10
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Friction Threshold
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={uxBlueprint.frictionThreshold * 10}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {uxBlueprint.frictionThreshold}/10
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Prompt DNA */}
        <Accordion defaultExpanded={expanded}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PromptDNAIcon color="success" />
              <Typography variant="subtitle1" fontWeight={600}>
                Prompt DNA
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Tone
                  </Typography>
                  <Chip label={promptDNA.tone} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Explanation Depth
                  </Typography>
                  <Chip label={promptDNA.explanationDepth} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Structure Preference
                  </Typography>
                  <Chip label={promptDNA.structurePreference} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Example Frequency
                  </Typography>
                  <Chip label={promptDNA.exampleFrequency} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Jargon Level
                  </Typography>
                  <Chip label={promptDNA.jargonLevel} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Code Preference
                  </Typography>
                  <Chip label={promptDNA.codePreference} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Verbosity
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={promptDNA.verbosity * 10}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {promptDNA.verbosity}/10
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Uncertainty Expression
                  </Typography>
                  <Chip label={promptDNA.uncertaintyExpression} size="small" sx={{ mt: 0.5 }} />
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Semantic Map */}
        {semanticMap && (
          <Accordion defaultExpanded={expanded}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SemanticMapIcon color="warning" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Semantic Map
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      Semantic Summary
                    </Typography>
                    <Typography variant="body2">{semanticMap.semanticMap.semanticSummary}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      Key Concepts
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                      {semanticMap.semanticMap.keyConcepts.map((concept) => (
                        <Chip key={concept} label={concept} size="small" />
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      Entities
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                      {semanticMap.semanticMap.entities.map((entity) => (
                        <Chip key={entity} label={entity} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      Themes
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                      {semanticMap.semanticMap.themes.map((theme) => (
                        <Chip key={theme} label={theme} size="small" />
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="overline" color="text.secondary">
                      Narrative Beats
                    </Typography>
                    <Stack component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                      {semanticMap.semanticMap.narrativeBeats.map((beat) => (
                        <Typography key={beat} component="li" variant="body2" sx={{ mb: 0.5 }}>
                          {beat}
                        </Typography>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Generated by {semanticMap.semanticMap.generatedBy} on{' '}
                    {new Date(semanticMap.semanticMap.generatedAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
